import React, { useState, useRef } from "react"
import { motion } from "framer-motion"
import { User, Plane, Globe, UserCircle } from "lucide-react"
import { MOTION_VARIANTS } from "./constants"
import { formatTelegramDate } from "../../utils/date/format"
import { chatItemId } from "../utils/parsers/messages"
import { daysColor } from "../utils/color/common"
import ChatPresenceIndicator from "./ChatPresenceIndicator"

const LeadChatItem = ({
  chat,
  index,
  selectedChat,
  onSelectChat,
  t,
  onShowTooltip,
  onHideTooltip,
  presenceUsers,
  isAdmin,
}) => {
  const msgRef = useRef(null)

  const handleMouseEnter = () => {
    if (!chat.message || !msgRef.current) return
    const rect = msgRef.current.getBoundingClientRect()
    onShowTooltip({ message: chat.message, top: rect.top + rect.height / 2, left: rect.right + 8 })
  }

  return (
    <motion.div
      custom={index}
      variants={MOTION_VARIANTS.item}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      onClick={() => onSelectChat(chat)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={onHideTooltip}
      className={`flex items-start gap-3 px-3 py-3 cursor-pointer transition-colors hover:bg-slate-50 border-b border-slate-100 last:border-0 ${
        selectedChat && chatItemId(selectedChat) === chatItemId(chat)
          ? "bg-blue-50 hover:bg-blue-50"
          : ""
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="flex flex-wrap gap-1 flex-1 min-w-0">
            <button className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors">
              #{chat.bundle_id}
            </button>

            {chat.lead_name && (
              <button className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors">
                <UserCircle className="w-3 h-3 flex-shrink-0" />
                <span className="max-w-[100px] truncate">{chat.lead_name}</span>
              </button>
            )}

            {chat.state_name && (
              <button className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors">
                <Globe className="w-3 h-3 flex-shrink-0" />
                <span className="max-w-[80px] truncate">{chat.state_name}</span>
              </button>
            )}

            {chat.manager_name && (
              <button className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors">
                <User className="w-3 h-3 flex-shrink-0" />
                <span className="max-w-[80px] truncate">{chat.manager_name}</span>
              </button>
            )}

            {chat.lead_status_name && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
                style={{
                  backgroundColor: chat.lead_status_bg || "#f1f5f9",
                  color: chat.lead_status_color || "#475569",
                }}
              >
                {chat.lead_status_name}
              </span>
            )}

            {chat.days_to_flight != null && (
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${daysColor(chat.days_to_flight)}`}
              >
                <Plane className="w-3 h-3 flex-shrink-0" />
                {chat.days_to_flight}д
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <ChatPresenceIndicator isAdmin={isAdmin} presenceUsers={presenceUsers} />
            <span className="text-xs text-slate-400 mt-0.5">
              {formatTelegramDate(chat.created_at)}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <p
            ref={msgRef}
            className="text-xs text-slate-400 line-clamp-1 flex-1"
            dangerouslySetInnerHTML={{ __html: chat.message }}
          />
          {chat.unread_count > 0 && (
            <div className="ml-2 min-w-[20px] h-5 px-1.5 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-medium">{chat.unread_count}</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  )
}

export default LeadChatItem
